import { useMutation, useQuery } from '@tanstack/vue-query'
import type {
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
} from '@@/api/types'
import { useApi } from '@/composables/useApi.js'
import { useAddToast } from '@/composables/useToast.js'
import useTranslation from '@/composables/useTranslation.js'

const CUSTOM_INTEGRATIONS_URL =
  '/{provet_id}/api/custom_integrations/for_object'

const CUSTOM_INTEGRATION_TRIGGER_URL =
  '/{provet_id}/api/custom_integrations/{id}/trigger'

export type CustomIntegrationsResponse = ResponseByPath<
  'get',
  typeof CUSTOM_INTEGRATIONS_URL
>

export type CustomIntegrationAttributes = SchemaAttributes<'CustomIntegration'>

export type IntegrationTypeName =
  CustomIntegrationAttributes['custom_integration_type']

export type IntegrationAction = CustomIntegrationAttributes['action']

export type IntegrationMethod = CustomIntegrationAttributes['method']

export type CustomIntegration = {
  id: string
  name?: string
  description?: string
  url?: string
  action?: IntegrationAction
  method?: IntegrationMethod
  actionParameters?: string
  parameterName?: string
  parameterValue?: number
  exposeOrganizationInfo?: boolean
  provetEnv?: string
  verificationTimestamp?: string
  verificationHash?: string
}

export type CustomIntegrationsQueryParams = QueryParamsByPath<
  'get',
  typeof CUSTOM_INTEGRATIONS_URL
>

export const OBJECT_TYPE = {
  CLIENT: 0,
  PATIENT: 1,
  INVOICE: 2,
  CONSULTATION: 3,
  LABORATORY_REFERRAL: 4,
  APPOINTMENTS_CALENDAR: 5,
  SHIFTS_CALENDAR: 6,
  DIAGNOSTIC_IMAGING: 7,
  REMINDER: 8,
  HEALTH_PLAN: 9,
  ONLINE_PRESCRIPTION_CLIENT: 10,
  ONLINE_PRESCRIPTION_PATIENT: 11,
  ONLINE_PRESCRIPTION_CONSULTATION: 12,
  HOME_DELIVERY: 13,
} as const satisfies Record<IntegrationTypeName, number>

/**
 * Object type codes for custom integrations (derived from OBJECT_TYPE constant)
 */
export type ObjectType = (typeof OBJECT_TYPE)[keyof typeof OBJECT_TYPE]

export const customIntegrationsKeys = {
  all: ['custom-integrations'] as const,
  detail: (objectType: MaybeRef<ObjectType>, objectId: MaybeRef<string>) =>
    [...customIntegrationsKeys.all, objectType, objectId] as const,
}

const normalizeData = (
  response: CustomIntegrationsResponse,
): CustomIntegration[] => {
  return (response.data || []).map((integration) => {
    const {
      name,
      description,
      url,
      action,
      method,
      action_parameters,
      parameter_name,
      parameter_value,
      expose_organization_info,
      provet_env,
      verification_timestamp,
      verification_hash,
    } = integration.attributes || {}

    return {
      id: integration.id,
      name,
      description,
      url,
      action,
      method,
      actionParameters: action_parameters,
      parameterName: parameter_name,
      parameterValue: parameter_value ?? undefined,
      exposeOrganizationInfo: expose_organization_info,
      provetEnv: provet_env ?? undefined,
      verificationTimestamp: verification_timestamp ?? undefined,
      verificationHash: verification_hash ?? undefined,
    }
  })
}

export const useCustomIntegrationsQuery = ({
  objectType,
  objectId,
  enabled = true,
}: {
  objectType: MaybeRef<ObjectType>
  objectId: MaybeRef<string>
  enabled?: MaybeRef<boolean>
}) => {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: customIntegrationsKeys.detail(objectType, objectId),
    enabled,
    queryFn: () =>
      api((client) =>
        client.GET(CUSTOM_INTEGRATIONS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
            },
            query: {
              object_type: unref(objectType),
              object_id: Number(unref(objectId)),
            },
          },
        }),
      ),
    select: (response) => normalizeData(response),
  })

  return reactive(query)
}

type TriggerCustomIntegrationParams = {
  integrationId: string
  objectId: string
}

/**
 * Mutation hook to trigger a BACKGROUND custom integration.
 * Calls POST /{provet_id}/api/custom_integrations/{integration_id}/trigger
 * with object_id in the request body.
 */
export function useTriggerCustomIntegrationMutation() {
  const api = useApi()
  const route = useRoute()
  const addToast = useAddToast()
  const tPage = useTranslation('pages.consultation')

  return useMutation({
    mutationFn: ({ integrationId, objectId }: TriggerCustomIntegrationParams) =>
      api((client) =>
        client.POST(CUSTOM_INTEGRATION_TRIGGER_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              id: integrationId,
            },
          },
          body: {
            data: {
              type: 'custom-integration-trigger',
              attributes: {
                object_id: Number(objectId),
              },
            },
          },
        }),
      ),
    onSuccess: () => addToast(tPage('integrations.triggerSuccess')),
    onError: (error) =>
      addToast(
        typeof error.cause === 'string'
          ? error.cause
          : tPage('integrations.triggerError'),
        { variant: 'danger' },
      ),
  })
}
